/**
 * DropZone — área de nova carga (RF-M2-007, RN-M2-003; spec `seletor-de-arquivos-na-carga`).
 * Arrastar: pastas percorridas por `webkitGetAsEntry()` (C-M11), arquivos soltos com o nome.
 * Escolher, como no protótipo: "escolha arquivos" (seletor de arquivos, filtrado pelas
 * extensões de `formatos.json`) e "escolha uma pasta" (`webkitdirectory`). O resto da área
 * não abre seletor — antes o clique abria só o de pasta, que no Windows não lista arquivos.
 * Em todos, o caminho relativo é preservado.
 */

import { useCallback, useRef, useState } from 'react';
import { ACCEPT_SELETOR_ARQUIVOS } from '../services/formatos';
import { lerItensArrastados, ArquivoSelecionado, selecionadosDoInput } from '../services/pastaArrastada';

export interface DropZoneProps {
  readonly onArquivos: (arquivos: ArquivoSelecionado[]) => void;
  readonly desabilitado?: boolean;
}

export function DropZone({ onArquivos, desabilitado }: DropZoneProps): JSX.Element {
  const arquivosRef = useRef<HTMLInputElement>(null);
  const pastaRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  const onDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setDragOver(false);
      if (desabilitado) {
        return;
      }
      // Lista de itens lida no próprio evento (o navegador a invalida depois).
      void lerItensArrastados(e.dataTransfer.items).then((arquivos) => {
        if (arquivos.length > 0) {
          onArquivos(arquivos);
        }
      });
    },
    [onArquivos, desabilitado],
  );

  const aoEscolher = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      // A lista primeiro: limpar o `value` esvazia a `FileList` do input.
      const files = e.target.files ? Array.from(e.target.files) : [];
      // Sem isto, escolher de novo o mesmo arquivo/pasta não dispara `change` (Req 3 AC4).
      e.target.value = '';
      if (files.length > 0) {
        onArquivos(selecionadosDoInput(files));
      }
    },
    [onArquivos],
  );

  return (
    <div
      className={`drop${dragOver ? ' drag-over' : ''}`}
      role="group"
      aria-label="Nova carga: arraste uma pasta ou arquivos"
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
    >
      <p>
        <strong>Arraste uma pasta ou arquivos aqui</strong>, ou{' '}
        <button type="button" className="drop-acao" onClick={() => arquivosRef.current?.click()} disabled={desabilitado}>
          escolha arquivos
        </button>
        {' · '}
        <button type="button" className="drop-acao" onClick={() => pastaRef.current?.click()} disabled={desabilitado}>
          escolha uma pasta
        </button>
        . As subpastas são preservadas.
      </p>
      <p className="drop-nota">
        Ao escolher uma pasta, o navegador pede confirmação e todos os arquivos dela, com os das subpastas, entram na
        carga.
      </p>
      <input
        ref={arquivosRef}
        type="file"
        multiple
        accept={ACCEPT_SELETOR_ARQUIVOS}
        hidden
        onChange={aoEscolher}
        disabled={desabilitado}
        data-testid="seletor-arquivos"
      />
      <input
        ref={pastaRef}
        type="file"
        multiple
        // @ts-expect-error — webkitdirectory não está nos tipos padrão de input.
        webkitdirectory=""
        hidden
        onChange={aoEscolher}
        disabled={desabilitado}
        data-testid="seletor-pasta"
      />
    </div>
  );
}
